'use client';

import React, { useState, useCallback, useMemo, memo } from 'react';
import Image from 'next/image';

interface AppImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  fill?: boolean;
  sizes?: string;
  onClick?: () => void;
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  unoptimized?: boolean;
  [key: string]: any;
}

/** Camera SVG icon shown when an image fails to load */
function CameraFallback() {
  return (
    <div className="img-broken-fallback">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
      <span
        style={{
          fontSize: '10px',
          fontFamily: 'Jost, sans-serif',
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#94a3b8',
        }}
      >
        Image Unavailable
      </span>
    </div>
  );
}

const AppImage = memo(function AppImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  quality = 85,
  placeholder = 'empty',
  blurDataURL,
  fill = false,
  sizes,
  onClick,
  fallbackSrc,
  loading = 'lazy',
  unoptimized = false,
  ...props
}: AppImageProps) {
  const [imgSrc, setImgSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Sync state if src changes
  React.useEffect(() => {
    setImgSrc(src);
    setHasError(false);
    setIsLoading(true);
  }, [src]);

  const isExternalUrl = useMemo(() => typeof imgSrc === 'string' && imgSrc.startsWith('http'), [imgSrc]);
  const resolvedUnoptimized = unoptimized || isExternalUrl;

  const handleError = useCallback(() => {
    const fallback = fallbackSrc || '/uploads/placeholder.jpg';
    if (imgSrc !== fallback) {
      setImgSrc(fallback);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  }, [imgSrc, fallbackSrc]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const imageClassName = useMemo(() => {
    const classes = [className];
    if (onClick) classes.push('cursor-pointer hover:opacity-90 transition-opacity duration-200');
    return classes.filter(Boolean).join(' ');
  }, [className, onClick]);

  const imageProps = useMemo(() => {
    const baseProps: any = {
      src: imgSrc,
      alt,
      className: imageClassName,
      quality,
      placeholder,
      unoptimized: resolvedUnoptimized,
      onError: handleError,
      onLoad: handleLoad,
      onClick,
    };

    if (priority) {
      baseProps.priority = true;
    } else {
      baseProps.loading = loading;
    }

    if (blurDataURL && placeholder === 'blur') {
      baseProps.blurDataURL = blurDataURL;
    }

    return baseProps;
  }, [
    imgSrc,
    alt,
    imageClassName,
    quality,
    placeholder,
    blurDataURL,
    resolvedUnoptimized,
    priority,
    loading,
    handleError,
    handleLoad,
    onClick,
  ]);

  if (fill) {
    return (
      <div className="relative" style={{ width: '100%', height: '100%' }}>
        {/* Shimmer shown while loading */}
        {isLoading && !hasError && <div className="shimmer-placeholder" />}

        {/* Camera fallback shown on error */}
        {hasError && <CameraFallback />}

        {/* The actual image (hidden via opacity until loaded) */}
        {!hasError && (
          <Image
            {...imageProps}
            fill
            sizes={sizes || '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'}
            style={{
              objectFit: 'cover',
              opacity: isLoading ? 0 : 1,
              transition: 'opacity 0.45s ease',
            }}
            {...props}
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative" style={{ width: width || 400, height: height || 300, flexShrink: 0 }}>
      {/* Shimmer shown while loading */}
      {isLoading && !hasError && <div className="shimmer-placeholder" />}

      {/* Camera fallback shown on error */}
      {hasError && <CameraFallback />}

      {/* The actual image */}
      {!hasError && (
        <Image
          {...imageProps}
          width={width || 400}
          height={height || 300}
          sizes={sizes}
          style={{
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.45s ease',
          }}
          {...props}
        />
      )}
    </div>
  );
});

AppImage.displayName = 'AppImage';

export default AppImage;
